import { supabase } from '../lib/supabase'
import { AircraftTransaction } from '../types/database'

/**
 * Service for managing overrides specific to aircraft transaction snapshots.
 * This service handles override data that is independent of the main aircraft overrides.
 */
export class TransactionOverridesService {
  private static readonly TABLE_NAME = 'aircraft_transactions'

  /**
   * Get transaction override data for a specific transaction
   */
  static async getByTransactionId(transactionId: string): Promise<AircraftTransaction | null> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('*')
        .eq('transaction_id', transactionId)
        .maybeSingle()

      if (error) {
        throw error
      }

      return data
    } catch (error) {
      throw error
    }
  }

  /**
   * Get transaction override data for multiple transactions
   */
  static async getByTransactionIds(transactionIds: string[]): Promise<Map<string, AircraftTransaction>> {
    try {
      if (!transactionIds || transactionIds.length === 0) {
        return new Map<string, AircraftTransaction>()
      }

      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('*')
        .in('transaction_id', transactionIds)

      if (error) {
        throw error
      }

      const transactionMap = new Map<string, AircraftTransaction>()
      if (data && Array.isArray(data)) {
        data.forEach(transaction => {
          if (transaction && transaction.transaction_id) {
            transactionMap.set(transaction.transaction_id, transaction)
          }
        })
      }

      return transactionMap
    } catch (error) {
      throw error
    }
  }

  /**
   * Update override data for a specific transaction snapshot
   * This only updates the override fields, not the core transaction data
   */
  static async updateOverrides(
    transactionId: string,
    overrideData: Partial<AircraftTransaction>
  ): Promise<AircraftTransaction> {
    try {
      // Extract only the override fields that can be updated
      const allowedOverrideFields = {
        // Pricing fields
        sale_price: overrideData.sale_price,
        asking_price: overrideData.asking_price,
        take_price: overrideData.take_price,
        external_asking_price: overrideData.external_asking_price,
        external_take_price: overrideData.external_take_price,
        external_sale_price: overrideData.external_sale_price,
        
        // Technical specifications
        aftt: overrideData.aftt,
        tbo: overrideData.tbo,
        landings: overrideData.landings,
        pax: overrideData.pax,
        
        // Location data
        base_airport: overrideData.base_airport,
        base_city: overrideData.base_city,
        base_state: overrideData.base_state,
        base_country: overrideData.base_country,
        
        // Status fields
        damage_history: overrideData.damage_history,
        emp: overrideData.emp,
        status: overrideData.status,
        maintenance_program: overrideData.maintenance_program,
        for_sale: overrideData.for_sale,
        leased: overrideData.leased,
        
        // Owner/Broker data
        owner: overrideData.owner,
        broker: overrideData.broker,
        owner_custom: overrideData.owner_custom,
        broker_custom: overrideData.broker_custom,
        
        // Date fields
        date_listed: overrideData.date_listed,
        date_purchased: overrideData.date_purchased,
        
        // Custom columns
        custom_1_value: overrideData.custom_1_value,
        custom_2_value: overrideData.custom_2_value,
        custom_3_value: overrideData.custom_3_value,
        
        // Options
        options: overrideData.options,
        
        // Notes
        transaction_notes: overrideData.transaction_notes
      }

      // Remove undefined values
      const updateData = Object.fromEntries(
        Object.entries(allowedOverrideFields).filter(([_, value]) => value !== undefined)
      )

      // Add updated_by field
      const user = await supabase.auth.getUser()
      if (user.data.user) {
        updateData.updated_by = user.data.user.id
      }

      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .update(updateData)
        .eq('transaction_id', transactionId)
        .select()
        .single()

      if (error) {
        throw error
      }

      return data
    } catch (error) {
      throw error
    }
  }

  /**
   * Update sold price for a transaction (both internal and external)
   */
  static async updateSoldPrice(
    transactionId: string,
    internalPrice?: number | null,
    externalPrice?: number | null
  ): Promise<AircraftTransaction> {
    try {
      const updateData: any = {}
      
      if (internalPrice !== undefined) {
        updateData.sale_price = internalPrice
      }
      
      if (externalPrice !== undefined) {
        updateData.external_sale_price = externalPrice
      }

      // Add updated_by field
      const user = await supabase.auth.getUser()
      if (user.data.user) {
        updateData.updated_by = user.data.user.id
      }

      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .update(updateData)
        .eq('transaction_id', transactionId)
        .select()
        .single()

      if (error) {
        throw error
      }

      return data
    } catch (error) {
      throw error
    }
  }

  /**
   * Update options for a transaction snapshot
   */
  static async updateOptions(
    transactionId: string,
    optionIds: string[]
  ): Promise<AircraftTransaction> {
    try {
      const user = await supabase.auth.getUser()
      const updateData: any = {
        options: optionIds
      }

      if (user.data.user) {
        updateData.updated_by = user.data.user.id
      }

      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .update(updateData)
        .eq('transaction_id', transactionId)
        .select()
        .single()

      if (error) {
        throw error
      }

      return data
    } catch (error) {
      throw error
    }
  }

  /**
   * Get all transactions for a specific aircraft (for historical tracking)
   */
  static async getByAircraftId(aircraftId: number): Promise<AircraftTransaction[]> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('*')
        .eq('aircraft_id', aircraftId)
        .order('transaction_date', { ascending: false })

      if (error) {
        throw error
      }

      return data || []
    } catch (error) {
      throw error
    }
  }

  /**
   * Check if a transaction can be edited (business rules)
   */
  static async canEditTransaction(transactionId: string): Promise<boolean> {
    try {
      const transaction = await this.getByTransactionId(transactionId)
      if (!transaction) {
        return false
      }

      // Add any business rules here
      // For now, all transactions can be edited
      return true
    } catch (error) {
      return false
    }
  }

  /**
   * Backfill a transaction snapshot with current override data from the aircraft
   * This is useful for snapshots that were created before override data was captured
   */
  static async backfillSnapshotWithOverrides(transactionId: string): Promise<AircraftTransaction> {
    try {
      // Get the current transaction snapshot
      const transaction = await this.getByTransactionId(transactionId)
      if (!transaction) {
        throw new Error(`Transaction not found: ${transactionId}`)
      }

      // Get current override data for this aircraft
      const { OverridesService } = await import('./overrides.service')
      const overrideData = await OverridesService.getByAircraftId(transaction.aircraft_id)

      if (!overrideData) {
        return transaction
      }

      // Get current aircraft options
      let aircraftOptions: string[] = []
      try {
        const { data: aircraftRecord } = await supabase
          .from('aircraft')
          .select('options')
          .eq('aircraft_id', transaction.aircraft_id)
          .maybeSingle()

        aircraftOptions = aircraftRecord?.options || []
      } catch (error) {
        // Could not fetch aircraft options for backfill
      }

      // Update the snapshot with current override data
      const backfillData = {
        // Pricing fields (only if not already set)
        asking_price: transaction.asking_price ?? overrideData.asking_price,
        take_price: transaction.take_price ?? overrideData.take_price,
        external_asking_price: transaction.external_asking_price ?? overrideData.external_asking_price,
        external_take_price: transaction.external_take_price ?? overrideData.external_take_price,

        // Technical specifications
        aftt: transaction.aftt ?? overrideData.aftt,
        tbo: transaction.tbo ?? overrideData.tbo,
        landings: transaction.landings ?? overrideData.landings,
        pax: transaction.pax ?? overrideData.pax,

        // Location data
        base_airport: transaction.base_airport ?? overrideData.base_airport,
        base_city: transaction.base_city ?? overrideData.base_city,
        base_state: transaction.base_state ?? overrideData.base_state,
        base_country: transaction.base_country ?? overrideData.base_country,

        // Status fields
        damage_history: transaction.damage_history ?? overrideData.damage_history,
        emp: transaction.emp ?? overrideData.emp,
        status: transaction.status ?? overrideData.status,
        maintenance_program: transaction.maintenance_program ?? overrideData.maintenance_program,
        for_sale: transaction.for_sale ?? overrideData.for_sale,
        leased: transaction.leased ?? overrideData.leased,

        // Owner/Broker data
        owner: transaction.owner ?? overrideData.owner,
        broker: transaction.broker ?? overrideData.broker,
        owner_custom: transaction.owner_custom ?? overrideData.owner_custom,
        broker_custom: transaction.broker_custom ?? overrideData.broker_custom,

        // Date fields
        date_listed: transaction.date_listed ?? overrideData.date_listed,
        date_purchased: transaction.date_purchased ?? overrideData.date_purchased,

        // Custom columns
        custom_1_value: transaction.custom_1_value ?? overrideData.custom_1_value,
        custom_2_value: transaction.custom_2_value ?? overrideData.custom_2_value,
        custom_3_value: transaction.custom_3_value ?? overrideData.custom_3_value,

        // Options
        options: transaction.options?.length ? transaction.options : aircraftOptions,

        // Update transaction notes to indicate backfill
        transaction_notes: transaction.transaction_notes
          ? `${transaction.transaction_notes} [Backfilled with override data]`
          : 'Backfilled with current override data'
      }

      return await this.updateOverrides(transactionId, backfillData)
    } catch (error) {
      throw error
    }
  }
}
